import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useWorkflowSurveysContext } from 'modules/surveys/hooks/useWorkflowSurveysContext'
import DeleteSurveyModal from 'modules/surveys/components/DeleteSurveyModal'
import { canEditSurvey } from 'modules/surveys/lib/canEditSurvey'
import surveys from 'modules/surveys/store/collections/v2/surveys'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  survey: Survey
}

function Settings({ survey }: Props) {
  const { navigation } = useNavigation()
  const location = useLocation()
  const navigate = useNavigate()
  const [showDestroyModal, toggleDestroyModal] = useToggle()
  const [open, toggle] = useToggle()
  const { canCreateForms, canManageSurveys } = useWorkflowSurveysContext()
  const canEdit = canEditSurvey(survey, canManageSurveys)
  const isClimateSurvey = survey.attributes.source === 'climate_survey'
  const isFromSmartSurveys = location.pathname.includes(navigation.engagement.smart_surveys.path)

  const actionRoute = () => {
    if (canEdit) {
      return navigation.processes.surveys.id.configuration.fullPath({ id: survey.attributes.id })
    } else {
      if (survey.isFinished) {
        return navigation.processes.surveys.id.responses.fullPath({ id: survey.attributes.id })
      } else {
        return navigation.processes.surveys.id.answer.fullPath({ id: survey.attributes.id })
      }
    }
  }

  const handleNavigate = () => {
    const route = actionRoute()
    if (isFromSmartSurveys) {
      navigate(route, { state: { fromSmartSurveys: true } })
    } else {
      navigate(route)
    }
  }

  const duplicateSurvey = async () => {
    app.setProgress({
      message: i18n.t('surveys.index.table.row.edit.duplicating'),
    })

    const { success, value } = await asyncAction(() => survey.rpc('duplicate'))
    app.setProgress(null)

    if (success) {
      surveys.add([value])
    }
  }

  const popoverOptions = () => {
    const options: Option[] = [
      {
        title: i18n.t('surveys.index.table.row.edit.delete'),
        type: 'wrong',
        onClick: toggleDestroyModal(true),
      },
    ]

    if (canCreateForms && !isClimateSurvey) {
      options.unshift({
        title: i18n.t('surveys.index.table.row.edit.duplicate'),
        type: 'primary',
        onClick: duplicateSurvey,
      })
    }

    return options
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      {canEdit && (
        <Popover
          attachment="top right"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button onClick={toggle(true)} type="secondary" iconName="ellipsis" />
          <PopoverContent onClose={toggle(false)} options={popoverOptions()} />
        </Popover>
      )}
      <Button onClick={handleNavigate} type="secondary" iconName="arrow-right" />
      {showDestroyModal && (
        <DeleteSurveyModal survey={survey} onClose={toggleDestroyModal(false)} />
      )}
    </Box>
  )
}

export default observer(Settings)
